import * as React from "react"
import { X, ArrowRight } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import type { GapDeviceUI } from "@/types/gap-device"
import type { UpdateGapDeviceRequest } from "@/types/gap-device"
import { useNumericInput, type NumericInputValue } from "@/hooks/useNumericInput"

export type Device = GapDeviceUI

interface DeviceEditPanelProps {
  device: GapDeviceUI | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (deviceId: string, data: UpdateGapDeviceRequest) => Promise<void>
  isSaving?: boolean
}

interface FormData {
  vendorDeviceId: string
  vendorName: string
  areaCode: string
  necThreshold: NumericInputValue
  necThresholdUseDefault: boolean
  userInstructionsEnabled: boolean
  userInstructionsUseDefault: boolean
}

export function DeviceEditPanel({ device, open, onOpenChange, onSave, isSaving = false }: DeviceEditPanelProps) {
  const [formData, setFormData] = React.useState<FormData>({
    vendorDeviceId: "",
    vendorName: "",
    areaCode: "",
    necThreshold: "",
    necThresholdUseDefault: false,
    userInstructionsEnabled: false,
    userInstructionsUseDefault: false,
  })
  const [originalData, setOriginalData] = React.useState<FormData | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (device) {
      const initialData: FormData = {
        vendorDeviceId: device.vendorDeviceId || `VND-${device.id}`,
        vendorName: device.vendorName,
        areaCode: device.areaCode || "",
        necThreshold: Number.parseInt(device.necThreshold) || 85,
        necThresholdUseDefault: device.necThresholdUseDefault,
        userInstructionsEnabled: device.userInstructionsEnabled,
        userInstructionsUseDefault: device.userInstructionsUseDefault,
      }
      setFormData(initialData)
      setOriginalData(initialData)
      setErrors({})
    }
  }, [device])

  React.useEffect(() => {
    if (open && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const necThresholdInput = useNumericInput({
    min: 0,
    max: 10000,
    integer: true,
    value: formData.necThreshold,
    validate: (value) => {
      if (formData.necThresholdUseDefault) return undefined
      if (value === "" || !Number.isInteger(value) || value < 0 || value > 10000) {
        return "Threshold is required when not using default, and must be a valid number between 0 and 10000"
      }
      return undefined
    },
  })

  const hasChanges = React.useMemo(() => {
    if (!originalData) return false
    return (
      formData.vendorDeviceId !== originalData.vendorDeviceId ||
      formData.vendorName !== originalData.vendorName ||
      formData.areaCode !== originalData.areaCode ||
      formData.necThreshold !== originalData.necThreshold ||
      formData.necThresholdUseDefault !== originalData.necThresholdUseDefault ||
      formData.userInstructionsEnabled !== originalData.userInstructionsEnabled ||
      formData.userInstructionsUseDefault !== originalData.userInstructionsUseDefault
    )
  }, [formData, originalData])

  const changes = React.useMemo(() => {
    if (!originalData) return []
    const changeList: { field: string; oldValue: string; newValue: string }[] = []

    if (formData.vendorDeviceId !== originalData.vendorDeviceId) {
      changeList.push({
        field: "Vendor Device ID",
        oldValue: originalData.vendorDeviceId,
        newValue: formData.vendorDeviceId,
      })
    }
    if (formData.vendorName !== originalData.vendorName) {
      changeList.push({
        field: "Vendor Name",
        oldValue: originalData.vendorName,
        newValue: formData.vendorName,
      })
    }
    if (formData.areaCode !== originalData.areaCode) {
      changeList.push({
        field: "Area Code",
        oldValue: originalData.areaCode || "",
        newValue: formData.areaCode || "",
      })
    }
    if (formData.necThreshold !== originalData.necThreshold || formData.necThresholdUseDefault !== originalData.necThresholdUseDefault) {
      changeList.push({
        field: "NEC Threshold",
        oldValue: originalData.necThresholdUseDefault ? "Use default" : String(originalData.necThreshold),
        newValue: formData.necThresholdUseDefault ? "Use default" : String(formData.necThreshold),
      })
    }
    if (formData.userInstructionsEnabled !== originalData.userInstructionsEnabled || formData.userInstructionsUseDefault !== originalData.userInstructionsUseDefault) {
      const oldVal = originalData.userInstructionsUseDefault
        ? "Use default"
        : originalData.userInstructionsEnabled
          ? "Enabled"
          : "Disabled"
      const newVal = formData.userInstructionsUseDefault
        ? "Use default"
        : formData.userInstructionsEnabled
          ? "Enabled"
          : "Disabled"
      changeList.push({
        field: "User Instructions",
        oldValue: oldVal,
        newValue: newVal,
      })
    }

    return changeList
  }, [formData, originalData])

  const handleClose = React.useCallback((forceClose = false) => {
    if (hasChanges && !forceClose) {
      setShowConfirmDialog(true)
    } else {
      onOpenChange(false)
    }
  }, [hasChanges, onOpenChange])

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false)
    onOpenChange(false)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.vendorDeviceId.trim()) {
      newErrors.vendorDeviceId = "Vendor Device ID is required"
    }
    if (!formData.vendorName.trim()) {
      newErrors.vendorName = "Vendor Name is required"
    }
    if (!formData.areaCode.trim()) {
      newErrors.areaCode = "Area Code is required"
    }
    if (!formData.necThresholdUseDefault) {
      if (necThresholdInput.error) {
        newErrors.necThreshold = necThresholdInput.error
      } else if (formData.necThreshold === "") {
        newErrors.necThreshold = "Threshold is required when not using default, and must be a valid number between 0 and 10000"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!device || !validateForm()) return

    const updateData: UpdateGapDeviceRequest = {
      clientId: device.clientId,
      vendorDeviceId: formData.vendorDeviceId,
      vendorName: formData.vendorName,
      areaCode: formData.areaCode || undefined,
      necMatchScoreThreshold: formData.necThresholdUseDefault
        ? { useDefault: true }
        : { useDefault: false, value: Math.round(formData.necThreshold as number) },
      featureFlagUserInstructions: formData.userInstructionsUseDefault
        ? { useDefault: true }
        : { useDefault: false, value: formData.userInstructionsEnabled },
    }

    await onSave(device.deviceId, updateData)
    onOpenChange(false)
  }

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        e.preventDefault()
        handleClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, handleClose])

  if (!device) return null

  return (
    <>
      <Sheet open={open} onOpenChange={(newOpen) => !newOpen && handleClose()}>
        <SheetContent
          side="right"
          className="w-full sm:w-[480px] sm:max-w-[480px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
        >
          {/* Header */}
          <SheetHeader className="px-6 py-5 border-b border-border">
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-lg font-semibold text-foreground">Edit Device</SheetTitle>
                <SheetDescription className="font-mono text-muted-foreground mt-1">{device.deviceId}</SheetDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleClose()}
                className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </SheetHeader>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Device Identification Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Device Identification
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Device ID (read-only) */}
              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">Device ID</Label>
                <Input
                  value={device.deviceId}
                  disabled
                  className="font-mono bg-muted/50 border-input text-muted-foreground cursor-not-allowed"
                />
              </div>

              {/* Vendor Device ID */}
              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  Vendor Device ID <span className="text-destructive">*</span>
                </Label>
                <Input
                  ref={firstInputRef}
                  value={formData.vendorDeviceId}
                  onChange={(e) => setFormData({ ...formData, vendorDeviceId: e.target.value })}
                  className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    errors.vendorDeviceId ? "border-destructive" : ""
                  }`}
                />
                {errors.vendorDeviceId && <p className="text-xs text-destructive">{errors.vendorDeviceId}</p>}
              </div>

              {/* Vendor Name */}
              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  Vendor Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={formData.vendorName}
                  onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                  className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    errors.vendorName ? "border-destructive" : ""
                  }`}
                />
                {errors.vendorName && <p className="text-xs text-destructive">{errors.vendorName}</p>}
              </div>

              {/* Area Code */}
              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  Area Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={formData.areaCode}
                  onChange={(e) => setFormData({ ...formData, areaCode: e.target.value })}
                  placeholder="e.g., 212"
                  className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    errors.areaCode ? "border-destructive" : ""
                  }`}
                />
                {errors.areaCode && <p className="text-xs text-destructive">{errors.areaCode}</p>}
              </div>
            </div>

            {/* Client Association Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Client Association
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Client ID (read-only) */}
              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">Client ID</Label>
                <Input
                  value={device.clientId}
                  disabled
                  className="font-mono bg-muted/50 border-input text-muted-foreground cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">Client ID cannot be changed</p>
              </div>
            </div>

            {/* Configuration Settings Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Configuration Settings
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* NEC Match Score Threshold */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[13px] text-muted-foreground font-medium">
                    NEC Match Score Threshold <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="necThresholdUseDefault"
                      checked={formData.necThresholdUseDefault}
                      onChange={(e) => {
                        setFormData({ ...formData, necThresholdUseDefault: e.target.checked })
                      }}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    <Label htmlFor="necThresholdUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                      Use default
                    </Label>
                  </div>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  step={1}
                  value={necThresholdInput.inputValue}
                  onChange={(e) => {
                    necThresholdInput.handleChange(e)
                    const nextRaw = e.target.value
                    let nextValue: NumericInputValue = ""
                    if (nextRaw !== "") {
                      const parsed = Number(nextRaw)
                      nextValue = Number.isNaN(parsed) ? "" : parsed
                    }
                    setFormData((prev) => ({
                      ...prev,
                      necThreshold: nextValue,
                    }))
                  }}
                  onBlur={necThresholdInput.handleBlur}
                  disabled={formData.necThresholdUseDefault}
                  autoComplete="off"
                  className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] disabled:opacity-50 disabled:cursor-not-allowed ${
                    !formData.necThresholdUseDefault && necThresholdInput.error ? "border-destructive" : ""
                  }`}
                />
                {!formData.necThresholdUseDefault && necThresholdInput.error ? (
                  <p className="text-xs text-destructive">{necThresholdInput.error}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {formData.necThresholdUseDefault
                      ? "Will use the client's default threshold"
                      : "Enter an integer between 0 and 10000"}
                  </p>
                )}
              </div>

              {/* User Instructions Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-[13px] text-muted-foreground font-medium">User Instructions</Label>
                    <p className="text-xs text-muted-foreground">Enable user instruction feature flag</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="userInstructionsUseDefault"
                      checked={formData.userInstructionsUseDefault}
                      onChange={(e) => {
                        setFormData({ ...formData, userInstructionsUseDefault: e.target.checked })
                      }}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    <Label htmlFor="userInstructionsUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                      Use default
                    </Label>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    {formData.userInstructionsUseDefault ? "Will use client setting" : "Device-specific setting"}
                  </span>
                  <Switch
                    checked={formData.userInstructionsEnabled}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, userInstructionsEnabled: checked })
                    }
                    disabled={formData.userInstructionsUseDefault}
                    className="data-[state=checked]:bg-primary disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Diff Preview Section */}
            {hasChanges && (
              <div className="space-y-3">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Changes to Apply</h3>
                <div className="bg-background/50 rounded-lg border border-border p-4 space-y-2">
                  {changes.map((change, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">{change.field}:</span>
                      <span className="text-muted-foreground line-through">{change.oldValue}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground/60" />
                      <span className="text-success">{change.newValue}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => handleClose()}
              disabled={isSaving}
              className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
            >
              {isSaving && <LoadingSpinner size="sm" className="mr-2" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground">
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
